import {
  Box,
  Flex,
  Grid,
  GridItem,
  Heading,
  Image,
  Paragraph,
  Tag,
  Text
} from '../../atoms';

export type ArticleLink = {
  title: string;
  imgPath: string;
  link: string;
  isAudio?: boolean;
  isAvailable?: boolean;
  tag: string;
  readingTime: string;
};

type Props = {
  articleLinks: ArticleLink[] | undefined;
  title: string;
  restTitle: string;
  subTitle: string;
  comingSoonLabel: string;
};

export function InspirationCornerComponent({
  title,
  restTitle,
  subTitle,
  comingSoonLabel,
  articleLinks
}: Props): JSX.Element {
  return (
    <Flex w="100%" mt="space_unit.x4">
      <Flex flexDir="column" w="100%">
        <Flex w="100%" mb="space_unit.x2" flexDir="column">
          <Flex mb="space_unit.x1">
            <Heading as="h2" variant="h4">
              <Text as="span" color="palette_blue.100">
                {title}
              </Text>{' '}
              {restTitle}
            </Heading>
          </Flex>
          <Paragraph>{subTitle}</Paragraph>
        </Flex>

        <Grid gap={6} w="100%" templateColumns="repeat(4, 1fr)">
          {articleLinks?.map((content, index) => {
            return (
              <InspirationCornerCard
                key={`card-${content.title}-${index}`}
                title={content.title}
                imgPath={content.imgPath}
                link={content.link}
                readingTime={content.readingTime}
                tag={content.tag}
                isAudio={content.isAudio}
                isAvailable={content.isAvailable}
                comingSoonLabel={comingSoonLabel}
              />
            );
          })}
        </Grid>
      </Flex>
    </Flex>
  );
}

export function InspirationCornerCard({
  imgPath,
  title,
  readingTime,
  tag,
  isAudio = false,
  isAvailable = true,
  link,
  comingSoonLabel
}: ArticleLink & { comingSoonLabel: string }): JSX.Element {
  return (
    <GridItem
      role="group"
      cursor="pointer"
      w="100%"
      flexDir="column"
      mb="space_unit.x2"
      as="a"
      href={link}
      target="_blank"
    >
      <Flex borderRadius="radii.x2" flexDir="column">
        <Box
          mb="space_unit.x2"
          borderRadius="radii.x2"
          h="132px"
          w="100%"
          overflow="hidden"
          position="relative"
        >
          {isAudio && (
            <Flex
              position="absolute"
              backgroundColor="#301E5F"
              borderRadius="6px"
              p="4px"
              top="12px"
              left="12px"
              w="32px"
              h="32px"
            >
              <Image
                src="https://cdn.payflip.be/payflip-cms/assets/headphone_f7fb9b61ac.svg"
                w="100%"
                h="100%"
              />
            </Flex>
          )}
          <Image
            objectFit="cover"
            w="100%"
            h="100%"
            src={imgPath}
            transition="all .3s ease-in-out"
            _groupHover={{
              transform: 'scale(1.1)'
            }}
          />
        </Box>
        {isAvailable ? (
          <Flex alignItems="center" h="24px">
            <Paragraph color="palette_gray.100" size="sm" mr="space_unit.x1">
              {tag}
            </Paragraph>
            <Paragraph color="palette_gray.60" size="xl" mr="space_unit.x1">
              &#183;
            </Paragraph>
            <Paragraph color="palette_gray.60" size="sm">
              {readingTime}
            </Paragraph>
          </Flex>
        ) : (
          <Flex>
            <Tag
              h="20px"
              color="palette_blue.100"
              backgroundColor="palette_blue.10"
              borderRadius="40px"
              size="sm"
            >
              {comingSoonLabel}
            </Tag>
          </Flex>
        )}

        <Paragraph size="md" fontWeight="bold">
          {title}
        </Paragraph>
      </Flex>
    </GridItem>
  );
}
